import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transportAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import LocationField from '../../components/LocationField';
import MobileStepWizard from '../../components/MobileStepWizard';

const CARGO_TYPES = [
  { key: 'furniture', label: 'أثاث', icon: '🛋️' },
  { key: 'building_materials', label: 'مواد بناء', icon: '🧱' },
  { key: 'commercial_goods', label: 'بضائع تجارية', icon: '📦' },
  { key: 'parcels', label: 'طرود', icon: '📮' },
  { key: 'equipment', label: 'معدات', icon: '⚙️' },
  { key: 'food_items', label: 'مواد غذائية', icon: '🍚' },
  { key: 'vehicle', label: 'سيارة', icon: '🚗' },
  { key: 'other', label: 'أخرى', icon: '📋' },
];

const VEHICLE_OPTIONS = [
  { key: '', label: 'دع مزود الخدمة يقترح المناسبة' },
  { key: 'rickshaw', label: 'تكتك' },
  { key: 'pickup', label: 'بيكب' },
  { key: 'bus', label: 'حافلة' },
  { key: 'light_truck', label: 'دفار' },
  { key: 'truck_5ton', label: 'شاحنة 5 طن' },
  { key: 'truck_10ton', label: 'شاحنة 10 طن' },
  { key: 'heavy_truck', label: 'شاحنة ثقيلة' },
  { key: 'trailer', label: 'تريلا' },
  { key: 'container_carrier', label: 'ناقلة اليات' },
];

const inputCls = 'w-full border-2 border-gray-200 hover:border-[#1E3A8A]/40 focus:border-[#1E3A8A] focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

const TransportRequestNewPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;
  const [form, setForm] = useState({
    fromLabel: '', toLabel: '', cargoCategory: 'commercial_goods', cargoDescription: '',
    weightKg: '', quantity: '', dimensions: '', vehiclePreference: '',
    loadingDate: '', loadingAssistance: false, unloadingAssistance: false,
    fromLat: null, fromLng: null, toLat: null, toLng: null,
  });

  const pickFrom = (lat, lng, label) => setForm((f) => ({ ...f, fromLat: lat, fromLng: lng, fromLabel: label }));
  const pickTo   = (lat, lng, label) => setForm((f) => ({ ...f, toLat: lat,   toLng: lng,   toLabel: label }));
  const [suggested, setSuggested] = useState(null);
  const [creating, setCreating]   = useState(false);
  const [error, setError]         = useState('');
  const [wizardStep, setWizardStep] = useState(0);

  const set = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const canSubmit = form.fromLabel.trim() && form.toLabel.trim() && form.cargoCategory;

  const handleCreate = async () => {
    if (!canSubmit) { setError('يرجى تحديد نقطة التحميل والتسليم ونوع البضاعة'); return; }
    setCreating(true); setError('');
    try {
      const res = await transportAPI.createRequest(form);
      setSuggested(res.data.data?.suggestedVehicle);
      navigate(`/transport/requests/${res.data.data.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر إنشاء طلب النقل');
    } finally { setCreating(false); }
  };

  const cargoSection = (
    <>
      <div className="flex gap-2 flex-wrap mb-4">
        {CARGO_TYPES.map((t) => (
          <button key={t.key} type="button" onClick={() => setForm((f) => ({ ...f, cargoCategory: t.key }))}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
              form.cargoCategory === t.key ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-[#1E3A8A]/50'
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <div className="mb-3"><label className={labelCls}>وصف البضاعة (اختياري)</label><textarea rows={2} className={inputCls} value={form.cargoDescription} onChange={set('cargoDescription')} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><label className={labelCls}>الوزن التقريبي (كجم)</label><input type="number" step="0.1" className={inputCls} value={form.weightKg} onChange={set('weightKg')} /></div>
        <div><label className={labelCls}>الكمية</label><input type="number" min="1" className={inputCls} value={form.quantity} onChange={set('quantity')} /></div>
        <div><label className={labelCls}>الأبعاد (اختياري)</label><input className={inputCls} placeholder="مثال: 2×1×1 م" value={form.dimensions} onChange={set('dimensions')} /></div>
      </div>
      {form.weightKg && (
        <p className="text-xs text-blue-700 mt-2">
          💡 بهذا الوزن، غالباً تحتاج: <strong>{VEHICLE_OPTIONS.find((v) => v.key === suggestedFor(form.weightKg))?.label || 'مركبة مناسبة'}</strong> — يمكنك ترك الاختيار للمزود أيضاً
        </p>
      )}
    </>
  );

  const routeSection = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <LocationField
          label="من *" placeholder="مثال: الخرطوم — السوق العربي"
          value={form.fromLabel} onChange={(v) => setForm((f) => ({ ...f, fromLabel: v }))}
          lat={form.fromLat} lng={form.fromLng} onPick={pickFrom}
          inputClassName={inputCls} labelClassName={labelCls}
          autoCloseOnPick
        />
        <LocationField
          label="إلى *" placeholder="مثال: ود مدني — السوق الكبير"
          value={form.toLabel} onChange={(v) => setForm((f) => ({ ...f, toLabel: v }))}
          lat={form.toLat} lng={form.toLng} onPick={pickTo}
          inputClassName={inputCls} labelClassName={labelCls}
          autoCloseOnPick
        />
      </div>
      <div><label className={labelCls}>تاريخ التحميل (اختياري)</label><input type="date" className={inputCls} value={form.loadingDate} onChange={set('loadingDate')} /></div>
    </>
  );

  const vehicleSection = (
    <>
      <div className="mb-3">
        <label className={labelCls}>نوع المركبة المفضل</label>
        <select className={inputCls} value={form.vehiclePreference} onChange={set('vehiclePreference')}>
          {VEHICLE_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
          <input type="checkbox" checked={form.loadingAssistance} onChange={set('loadingAssistance')} /> مساعدة في التحميل
        </label>
        <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
          <input type="checkbox" checked={form.unloadingAssistance} onChange={set('unloadingAssistance')} /> مساعدة في التنزيل
        </label>
      </div>
    </>
  );

  // ── Mobile wizard: the full journey in 2 steps — what to transport,
  //    then where + which vehicle (route and vehicle combined so the
  //    second screen finishes the whole request in one go). ──
  const wizardSteps = [
    { title: 'ماذا تريد أن تنقل؟', subtitle: 'اختر نوع البضاعة وتفاصيلها', isValid: !!form.cargoCategory, render: () => cargoSection },
    {
      title: 'من أين إلى أين؟',
      subtitle: 'حدد نقطة التحميل والتسليم، ونوع المركبة المناسب',
      isValid: !!(form.fromLabel.trim() && form.toLabel.trim()),
      render: () => (
        <div className="space-y-5">
          {routeSection}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="font-bold text-gray-800 mb-3 text-sm">نوع المركبة والمساعدة</h3>
            {vehicleSection}
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => { if (!isLoggedIn) navigate('/login?from=/transport/new'); }, [isLoggedIn, navigate]);
  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF]">
      <AppNavbar />

      {/* Desktop / tablet: existing single-page scrollable form, unchanged */}
      <div className="hidden md:block pb-16">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <h1 className="text-xl font-black text-[#1E3A8A] mb-1">🚚 طلب نقل بضائع</h1>
          <p className="text-sm text-gray-600 mb-6">صف ما تريد نقله وسيرسل لك مزودو النقل عروضهم — اختر ما يناسبك</p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

          <div className="space-y-5">
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-bold text-gray-800 mb-3">ماذا تريد أن تنقل؟</h2>
              {cargoSection}
            </section>

            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-bold text-gray-800 mb-3">نقطة التحميل والتسليم</h2>
              {routeSection}
            </section>

            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-bold text-gray-800 mb-3">نوع المركبة والمساعدة</h2>
              {vehicleSection}
            </section>

            <button type="button" onClick={handleCreate} disabled={creating || !canSubmit}
              className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3.5 rounded-xl transition-all">
              {creating ? '...جارٍ الإرسال' : 'إرسال الطلب لمزودي النقل'}
            </button>
          </div>
        </div>
        <SiteFooter navigate={navigate} />
      </div>

      {/* Mobile: step-by-step wizard, one screen per section */}
      <MobileStepWizard
        steps={wizardSteps}
        step={wizardStep}
        setStep={setWizardStep}
        onSubmit={handleCreate}
        submitting={creating}
        submitLabel="إرسال الطلب"
        error={error}
        onExit={() => navigate(-1)}
      />
      <MobileBottomNav />
    </div>
  );
};

// Mirrors the backend's suggestVehicleForWeight bracket table (transportController.js)
// — purely a client-side hint while typing, the real suggestion comes back with
// the created request; kept in sync manually since it's a tiny, stable lookup.
const suggestedFor = (weightKg) => {
  const w = parseFloat(weightKg) || 0;
  if (w <= 0) return null;
  if (w <= 500) return 'pickup';
  if (w <= 2000) return 'light_truck';
  if (w <= 5000) return 'truck_5ton';
  if (w <= 10000) return 'truck_10ton';
  return 'heavy_truck';
};

export default TransportRequestNewPage;
